import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PersonalDetailsPage } from './personal-details/personal-details.page';
import { HubSelectionPage } from './hub-selection/hub-selection.page';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.page.html',
  styleUrls: ['./onboarding.page.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    PersonalDetailsPage,
    HubSelectionPage,
    CommonModule,
  ],
})
export class OnboardingPage {
  @ViewChild(HubSelectionPage) hubSelectionComponent!: HubSelectionPage;

  personalDetailsForm: FormGroup;
  joinHubForm: FormGroup;
  createHubForm: FormGroup;

  currentStep = 1;
  loading = false;
  emailSent = false;
  environment = environment;

  hubSelectionMode: 'join' | 'create' | null = null;

  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _fb: FormBuilder,
    private _toast: ToastService,
    private _http: HttpClient
  ) {
    this.personalDetailsForm = this._fb.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      tel: ['', [Validators.required]],
      street: ['', [Validators.required]],
      postal: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      city: ['', [Validators.required]],
    });

    this.joinHubForm = this._fb.group({
      join_code: ['', [Validators.required, Validators.minLength(3)]],
    });

    this.createHubForm = this._fb.group({
      hub_name: ['', [Validators.required]],
      hub_website: [''],
      hub_description: [''],
      hub_type: ['', [Validators.required]],
      hub_postal: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      hub_city: ['', [Validators.required]],
      hub_tel: [''],
      hub_street: ['', [Validators.required]],
      avatar: [''], // Avatar URL will be set here
      is_24_7: [false]
    });
  }

  ngOnInit(): void {
    this.checkOnboardingState();
  }

  async checkOnboardingState() {
    this.loading = true;
    try {
      await this._authService.refreshClaims();
      this._authService.claims$.subscribe((claims) => {
        console.log(claims);
        this.processUserClaims(claims);
      });
    } finally {
      this.loading = false;
    }
  }

  async processUserClaims(claims: any) {
    
    
    if (claims?.regional_hub_assigned) {
      this._router.navigate(['/dashboard']);
      return;
    }
    
    if (claims?.regional_user_data_saved) {
      this.currentStep = 3;
      return;
    }

    if (claims?.email_verified) {
      this.currentStep = 2;
      return;
    }

    this.currentStep = 1;
  }

  async sendVerificationEmail() {
    this.emailSent = true;
    try {
      await firstValueFrom(
        this._http.post<any>(
          `${environment.apiUrl}/regional/users/email/verify`,
          {}
        )
      );
      this._toast.add({
        severity: 'success',
        summary: 'E-Mail gesendet',
        detail: 'Bitte überprüfe deine E-Mails, um deine Adresse zu bestätigen',
      });
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler beim Senden der E-Mail',
        detail: 'Bitte versuchen Sie es später erneut',
      });
      console.error('Error sending verification email:', error);
    } finally {
      this.emailSent = false;
    }
  }

  async savePersonalDetails() {
    if (this.personalDetailsForm.invalid) {
      Object.keys(this.personalDetailsForm.controls).forEach((key) => {
        const control: any = this.personalDetailsForm.get(key);
        control.markAsTouched();
        control.markAsDirty();
      });

      this._toast.add({
        severity: 'contrast',
        summary: 'Bitte alle Felder ausfüllen',
      });
      return;
    }

    this.loading = true;

    try {
      const result: any = await firstValueFrom(
        this._http.put<any>(
          `${environment.apiUrl}/regional/users`,
          this.personalDetailsForm.value
        )
      );

      await this._authService.refreshClaims();

      this._toast.add({
        severity: 'success',
        summary: 'Daten gespeichert',
        detail: 'Ihre persönlichen Daten wurden erfolgreich gespeichert',
      });

      this.currentStep = 3;
    } catch (error) {
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Speichern der Daten',
        detail: 'Bitte versuchen Sie es später erneut',
      });
      console.error('Error saving personal details:', error);
    } finally {
      this.loading = false;
    }
  }

  onHubSelectionChange(mode: string) {
    this.hubSelectionMode = mode as 'join' | 'create';
  }

  canCompleteOnboarding(): boolean {
    if (this.hubSelectionMode === 'join') {
      return this.joinHubForm.valid;
    } else if (this.hubSelectionMode === 'create') {
      return this.createHubForm.valid;
    }
    return false;
  }

  async completeOnboarding() {
    if (!this.canCompleteOnboarding()) {
      this._toast.add({
        severity: 'contrast',
        summary: 'Bitte alle Felder ausfüllen',
      });
      return;
    }

    this.loading = true;

    try {
      if (this.hubSelectionMode === 'join') {
        await this.joinExistingHub();
      } else if (this.hubSelectionMode === 'create') {
        await this.createNewHub();
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      this.loading = false;
    }
  }

  async joinExistingHub() {
    try {
      const result: any = await firstValueFrom(
        this._http.post<any>(
          `${environment.apiUrl}/regional/hubs/join`,
          this.joinHubForm.value
        )
      );
      
      await this._authService.refreshClaims();

      this._toast.add({
        severity: 'success',
        summary: 'Erfolgreich beigetreten!',
        detail: 'Sie sind jetzt Teil der Kommune',
      });

      this._router.navigate(['/dashboard']);
    } catch (error: any) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler beim Beitreten',
        detail: error?.error?.message || 'Ungültiger Code oder Fehler beim Beitreten',
      });
      throw error;
    }
  }

  async createNewHub() {
    try {
      // Upload avatar if selected
      let avatarUrl: string | null = null;
      if (this.hubSelectionComponent) {
        avatarUrl = await this.hubSelectionComponent.getHubAvatarUrl();
        if (avatarUrl) {
          this.createHubForm.patchValue({ avatar: avatarUrl });
        }
      }

      // Get opening hours from child component
      const openingHours = this.hubSelectionComponent?.getOpeningHoursData();
      
      const payload = {
        ...this.createHubForm.value,
        opening_hours: openingHours,
        timezone: 'Europe/Berlin'
      };
      console.log(payload);
      

      const result: any = await firstValueFrom(
        this._http.post<any>(
          `${environment.apiUrl}/regional/hubs`,
          payload
        )
      );

      await this._authService.refreshClaims();

      this._toast.add({
        severity: 'success',
        summary: 'Kommune erstellt!',
        detail: 'Ihre Kommune wurde erfolgreich erstellt',
      });

      // this._router.navigate(['/dashboard']);
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler beim Erstellen',
        detail: 'Die Kommune konnte nicht erstellt werden',
      });
      throw error;
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }
}